import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Row, Col, Select, Button, Modal, message } from 'antd';
import {
  PlusOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import AppLayout from '../components/common/Layout';
import ReportsTable from '../components/tables/ReportsTable';
import { reportApi } from '../api/reportApi';
import { teamApi } from '../api/teamApi';
import { useApi } from '../hooks/useApi';
import { FORM_STATUS_LABELS } from '../utils/validation';

const { Title, Text } = Typography;
const { confirm } = Modal;

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const { execute } = useApi();
  const navigate = useNavigate();

  const fetchMembers = async () => {
    try {
      const response = await teamApi.getAll();
      setMembers(response.data.members);
    } catch {
      setMembers([]);
    }
  };

  const fetchReports = async (params = filters) => {
    setLoading(true);
    try {
      const response = await reportApi.getAll(params);
      setReports(response.data.reports);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateReport = async () => {
    if (!selectedMember) return;
    await execute(() => reportApi.create(selectedMember), {
      successMessage: 'Отчёт создан',
      onSuccess: (data) => {
        setCreateOpen(false);
        setSelectedMember(null);
        fetchReports();
        if (data.report?.fillUrl) {
          navigator.clipboard.writeText(data.report.fillUrl);
          message.success('Ссылка скопирована в буфер обмена');
        }
      },
    });
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    message.success('Ссылка скопирована');
  };

  const handleDelete = (record) => {
    confirm({
      title: 'Удалить отчёт?',
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      onOk: async () => {
        await execute(() => reportApi.delete(record._id), {
          successMessage: 'Отчёт удалён',
          onSuccess: () => fetchReports(),
        });
      },
    });
  };

  return (
    <AppLayout>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3}>Отчёты</Title>
          <Text type="secondary">Все отчёты команды · срок заполнения 14 дней</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
          disabled={members.length === 0}
        >
          Создать отчёт
        </Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12}>
          <Select
            placeholder="Сотрудник"
            allowClear
            style={{ width: '100%' }}
            onChange={(value) => {
              const p = { ...filters, teamMemberId: value || undefined };
              setFilters(p);
              fetchReports(p);
            }}
            options={members.map((m) => ({ value: m._id, label: m.fullName }))}
          />
        </Col>
        <Col xs={24} sm={12}>
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: '100%' }}
            onChange={(value) => {
              const p = { ...filters, status: value || undefined };
              setFilters(p);
              fetchReports(p);
            }}
            options={Object.entries(FORM_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          />
        </Col>
      </Row>

      <ReportsTable
        reports={reports}
        loading={loading}
        onView={(r) => navigate(`/reports/${r._id}`)}
        onCopyLink={copyLink}
        onDelete={handleDelete}
      />

      <Modal
        title="Создать отчёт"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreateReport}
        okText="Создать и скопировать ссылку"
        okButtonProps={{ disabled: !selectedMember }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Будет создана персональная ссылка для заполнения. Срок действия — 14 дней.
        </Text>
        <Select
          placeholder="Выберите сотрудника"
          style={{ width: '100%' }}
          value={selectedMember}
          onChange={setSelectedMember}
          options={members.map((m) => ({ value: m._id, label: m.fullName }))}
        />
      </Modal>
    </AppLayout>
  );
};

export default Reports;
